import React, { useMemo } from 'react'
import {
  IonButton,
  IonButtons,
  IonCol,
  IonContent,
  IonFooter,
  IonGrid,
  IonItem,
  IonItemDivider,
  IonLabel,
  IonList,
  IonRow,
  IonTextarea,
  IonToolbar,
  useIonViewWillEnter,
} from '@ionic/react'
import { useDispatch, useSelector } from 'react-redux'
import { useTranslation } from 'react-i18next'
import { useHistory } from 'react-router'
import NumberPad from '../../../../components/numberPad/NumberPad.component'
import { numberPadKeysWithDecimal } from '../../../../components/numberPad/NumberPad.types'
import SingleSelect from '../../../../components/singleSelect/SingleSelect.component'
import { IOption } from '../../../../common/common.types'
import { IRootState } from '../../../../store'
import {
  saveVitals,
  setPainQualityComments,
  setPainQualityOnset,
  setPainQualityOnsetUnit,
  setPainQualityField1,
  setPainQualityField2,
  setPainQualityField3,
  setPainQualityField4,
  setPainQualityField5,
  setPainQualityField6,
  setPainQualityField7,
  setPainQuality,
} from '../../store/vitals.actions'
import { getVitalOptionsSelector } from '../../../options/selectors'
import { IVitalOptionsState } from '../../../options/store/options.types'
import { initialPainQualityState } from '../../store/vitals.reducers'
import { VitalsConstants } from '../../store/vitals.constants'
import Validation from '../../../../components/validation/Validation.component'

const VitalsPainQualityContent: React.FC = () => {
  const dispatch = useDispatch()
  const history = useHistory()
  const { t } = useTranslation()

  const painQuality = useSelector((state: IRootState) => state.vitals.painQuality)
  const painPNNV = useSelector((state: IRootState) => state.vitals.painPNNV)
  const vitalOptions: IVitalOptionsState = useSelector(getVitalOptionsSelector)

  const { painQualityOnsetUnitOptions, painQualityFieldOptions } = vitalOptions

  useIonViewWillEnter(() => {
    if (!painQuality) {
      dispatch(setPainQuality({ ...initialPainQualityState }))
    }
  })

  const disabled = !!painPNNV

  const onsetOnChange = (numbers: string[]) => {
    const nextOnset = numbers.length ? numbers.join('') : null
    dispatch(setPainQualityOnset(nextOnset))
  }

  const clearAll = () => {
    dispatch(setPainQuality({ ...initialPainQualityState }))
  }

  const painQualityHasData = useMemo(() => {
    return (
      !!painQuality &&
      ((!!painQuality.onset && painQuality.onset.trim() !== '') ||
        !!painQuality.onsetUnit ||
        !!painQuality.field1 ||
        !!painQuality.field2 ||
        !!painQuality.field3 ||
        !!painQuality.field4 ||
        !!painQuality.field5 ||
        !!painQuality.field6 ||
        !!painQuality.field7 ||
        (!!painQuality.comments && painQuality.comments.trim() !== ''))
    )
  }, [painQuality])

  if (!painQuality) return null
  const { onset, onsetUnit, field1, field2, field3, field4, field5, field6, field7, comments } = painQuality

  const fields = [
    { key: 'field1', value: field1, setAction: setPainQualityField1 },
    { key: 'field2', value: field2, setAction: setPainQualityField2 },
    { key: 'field3', value: field3, setAction: setPainQualityField3 },
    { key: 'field4', value: field4, setAction: setPainQualityField4 },
    { key: 'field5', value: field5, setAction: setPainQualityField5 },
    { key: 'field6', value: field6, setAction: setPainQualityField6 },
    { key: 'field7', value: field7, setAction: setPainQualityField7 },
  ]

  const commentsOnChange = (e: CustomEvent) => {
    if (e.detail.value !== painQuality.comments) {
      dispatch(setPainQualityComments(e.detail.value))
      dispatch(saveVitals())
    }
  }

  return (
    <>
      <IonContent className='ion-margin-bottom' fullscreen>
        <IonList>
          <IonItemDivider color='light' className='ion-padding'>
            <IonLabel className='grid-item-divider-label' color='medium'>
              {t('vitals.painQuality.title')}
            </IonLabel>
          </IonItemDivider>

          <IonGrid className='ion-no-padding'>
            <IonRow>
              <IonCol className='picklist'>
                <NumberPad
                  disabled={disabled}
                  elementId='PainQualityOnset'
                  dataTestId='PainQualityOnset'
                  values={onset ? onset.split('') : []}
                  label={t('vitals.painQuality.onset')}
                  validCharacters={numberPadKeysWithDecimal}
                  onNumPadClick={onsetOnChange}
                />
              </IonCol>
              <IonCol className='picklist'>
                <SingleSelect
                  disabled={disabled}
                  dataTestID='PainQualityOnsetUnit'
                  options={painQualityOnsetUnitOptions}
                  selectedOption={onsetUnit}
                  listHeader={t('vitals.painQuality.onsetUnit')}
                  label={t('vitals.painQuality.unit')}
                  action={(opt: IOption) => dispatch(setPainQualityOnsetUnit(opt))}
                />
              </IonCol>
            </IonRow>

            {fields.map(({ key, value, setAction }) => {
              const fieldConfig = painQualityFieldOptions?.[key]
              if (!fieldConfig?.label?.trim() || !fieldConfig?.options?.length) return null

              const { label, options } = fieldConfig

              return (
                <React.Fragment key={key}>
                  <hr className='field-divider' />
                  <IonRow>
                    <IonCol className='picklist'>
                      <SingleSelect
                        disabled={disabled}
                        dataTestID={`PainQuality${key}`}
                        options={options}
                        selectedOption={value}
                        listHeader={label}
                        label={label}
                        action={(opt: IOption) => dispatch(setAction(opt))}
                      />
                    </IonCol>
                  </IonRow>
                </React.Fragment>
              )
            })}

            <hr className='field-divider' />

            <IonItemDivider color='light' />

            <IonRow>
              <IonCol className='picklist'>
                <IonItem lines='none'>
                  <IonLabel position='stacked'>{t('vitals.painQuality.comments')}</IonLabel>
                  <div data-private={false}>
                    <IonTextarea
                      rows={6}
                      cols={20}
                      value={comments}
                      color='primary-alt'
                      onIonChange={commentsOnChange}
                      maxlength={4000}
                      autocapitalize='sentences'
                    />
                  </div>
                  <div className='footer'>
                    <Validation id={VitalsConstants.SET_PAIN_QUALITY_COMMENTS} />
                    <h1 className={comments?.length < 4000 ? 'text-color-blue-tint' : 'text-color-red'}>
                      {comments?.length}/4000
                    </h1>
                  </div>
                </IonItem>
              </IonCol>
            </IonRow>

            <IonRow>
              <IonLabel color='medium'></IonLabel>
            </IonRow>
          </IonGrid>
        </IonList>
      </IonContent>

      <IonFooter>
        <IonToolbar color='light' className='p-10'>
          <IonButtons className='btn-grps'>
            <IonButton
              className='btn btn-tablet'
              color='danger'
              fill='outline'
              expand='block'
              onClick={clearAll}
              disabled={!painQualityHasData}>
              {t('common.buttons.Clear')}
            </IonButton>
            <IonButton
              className='btn btn-tablet btn-primary'
              expand='block'
              disabled={!painQualityHasData}
              onClick={() => {
                dispatch(saveVitals())
                history.back()
              }}>
              {t('common.buttons.Save')}
            </IonButton>
          </IonButtons>
        </IonToolbar>
      </IonFooter>
    </>
  )
}

export default VitalsPainQualityContent
